// Status marks drawn on a piece's square. Corners carry different kinds of
// news: top left is lives and capture hooks, top right is learned abilities,
// bottom left is conditions, bottom right is royalty and the timers that end
// a piece. Every trait badge comes from the trait table. The board draws the
// badges in a layer over every piece, so no piece covers a badge.

import { PIECE_LEAVE_MS } from '../constants';
import type { CasterId } from '../content/casters';
import { BASE_ABILITIES } from '../engine/abilities';
import { HEART_PATH, type Trait, traitCount, traitsInCorner } from '../engine/traits';
import type { Ability, Owner, Piece, PieceKind, Pitch } from '../engine/types';
import { svg } from '../lib/svg';
import { createCasterPawnSmallParts, SMALL_PAWN_VIEW } from './CasterMark';
import { energyStarPath } from './energyStar';
import { createMiniGlyph, GLYPH_BOX, wearsCaster } from './PieceGlyph';
import { PITCH_ICON_PATHS } from './pitchIcon';

/** The disc and its marks are drawn at this radius, then the whole disc is scaled. */
const BADGE_RADIUS = 11;
/**
 * The disc on the board is 26 units wide. At 22 the mark in it was under the
 * 15px floor for text on a 97px square.
 */
const DISC_SCALE = 1.18;
/** Half of the box a disc takes, with its edge. A tag in the inspector uses it for its icon. */
export const DISC_BOX_RADIUS = 14.5;
const BADGE_STEP = 27;
const EDGE = 15;
const MINI_SCALE = 0.17;
/** The small count bubble on a timed badge. */
const COUNT_RADIUS = 8;
const COUNT_OFFSET = 9.5;
/** The height of the pawn of a caster on a badge, and how far the ground behind it goes past it. */
const CASTER_TILE_SIDE = 18;
const CASTER_TILE_PAD = 2;
/** How far the points of the star of energy reach in a disc. */
const ENERGY_MARK_RADIUS = 8.5;
/**
 * On a small board a badge at its usual size is a dot. There each corner shows one
 * badge, larger and farther from the edge, with a count of the others it stands for.
 */
const DENSE_SCALE = 1.6;
const DENSE_EDGE = 24;

const ABILITY_GLYPH: Partial<Record<Ability, PieceKind>> = {
  pawn: 'pawn',
  knight: 'knight',
  bishop: 'bishop',
  rook: 'rook',
  king: 'king',
};

export const CROWN_PATH = 'M-7 4 L-8 -4 L-3 0 L0 -6 L3 0 L8 -4 L7 4 Z';
const SPRING = 'M-6 6 L-2 -6 L2 6 L6 -6';
const BALL = 'M0 -6 A6 6 0 1 1 0 6 A6 6 0 1 1 0 -6 Z';

/**
 * The fuse of a bomb badge: a cap on the circle, a cord, and a spark at its
 * end. It goes up more than to the side and stays within `EDGE` of the center,
 * so the edge of the board does not cut it on the last file.
 */
const FUSE_CAP = 'M1.6 -10.3 L7.4 -7.5 L9.2 -11.1 L3.4 -13.9 Z';
const FUSE_CORD = 'M6.3 -12.5 Q9.5 -16 7 -19.5';
const FUSE_SPARK =
  'M7 -25 L8.4 -20.9 L12.5 -19.5 L8.4 -18.1 L7 -14 L5.6 -18.1 L1.5 -19.5 L5.6 -20.9 Z';

/** `key` names the badge for the life of the piece. `content` changes when its picture must. */
type BadgeSpec = {
  key: string;
  content: string;
  tone: string;
  symbol: SVGElement;
  count?: number;
  fuse?: boolean;
  /** The timer fires at the start of the next turn of the owner, so the badge blinks. */
  urgent?: boolean;
  /** The face of the badge, in place of a disc with the symbol. */
  face?: SVGGElement;
};

/** `others` is how many more badges of the corner this one stands for. */
type PlacedBadge = BadgeSpec & { x: number; y: number; scale: number; others: number };

/** Drawn under the circle, so the cap and the cord come out from behind it. */
const fuseParts = (): SVGElement[] => [
  svg('path', { class: 'badge__fuse-cap', d: FUSE_CAP }),
  svg('path', { class: 'badge__fuse-cord', d: FUSE_CORD }),
  svg('path', { class: 'badge__fuse-spark', d: FUSE_SPARK }),
];

/** The disc of a badge with its mark, and its fuse if it is a bomb. The board and the inspector both draw it. */
export const createBadgeDisc = (symbol: SVGElement, fuse = false): SVGGElement =>
  svg('g', { class: 'badge__disc', transform: `scale(${DISC_SCALE})` }, [
    ...(fuse ? fuseParts() : []),
    svg('circle', { r: BADGE_RADIUS }),
    symbol,
  ]);

/**
 * The badge of a piece that carries a caster: the small pawn of the caster,
 * as the plate of the caster has it, on a dark square with the edge of a
 * badge. A pawn wears its caster and has no badge, so the badge is on a pawn
 * that promoted. It was the small bishop until 2026-10-15, the square symbol
 * until 2026-10-14, and the first letter of
 * the name on a disc until 2026-10-13. Garrett: "instead of \"S\" on bishops,
 * lets put the symbol of the caster on that spot". The dark square is there
 * because three of the primary colors are about 1.2 to 1 on paper.
 * `data-caster` gives the group the colors.
 */
export const createCasterBadge = (casterId: string): SVGGElement => {
  const ground = CASTER_TILE_SIDE + CASTER_TILE_PAD * 2;
  return svg('g', { class: 'badge__disc', transform: `scale(${DISC_SCALE})` }, [
    svg('rect', {
      class: 'badge__ground',
      x: -ground / 2,
      y: -ground / 2,
      width: ground,
      height: ground,
    }),
    // The small pawn, as tall as the square, about its middle.
    svg(
      'g',
      {
        'data-caster': casterId,
        transform: `scale(${CASTER_TILE_SIDE / SMALL_PAWN_VIEW.height}) translate(${-(SMALL_PAWN_VIEW.x + SMALL_PAWN_VIEW.width / 2)} ${-(SMALL_PAWN_VIEW.y + SMALL_PAWN_VIEW.height / 2)})`,
      },
      createCasterPawnSmallParts(casterId as CasterId),
    ),
  ]);
};

/** The class of a badge face. The tone picks the color of the disc. */
export const badgeClass = (tone: string, urgent = false): string =>
  `badge badge--${tone}${urgent ? ' badge--urgent' : ''}`;

const badgeParts = (spec: PlacedBadge): SVGElement[] => {
  const children: SVGElement[] = [spec.face ?? createBadgeDisc(spec.symbol, spec.fuse)];
  if (spec.others > 0) {
    children.push(
      svg(
        'g',
        { class: 'badge__count', transform: `translate(${COUNT_OFFSET} ${-COUNT_OFFSET})` },
        [svg('circle', { r: COUNT_RADIUS }), svg('text', { y: 3.5 }, [`+${spec.others}`])],
      ),
    );
  }
  if (spec.count !== undefined) {
    children.push(
      svg('g', { class: 'badge__count', transform: `translate(${COUNT_OFFSET} ${COUNT_OFFSET})` }, [
        svg('circle', { r: COUNT_RADIUS }),
        svg('text', { y: 3.5 }, [String(spec.count)]),
      ]),
    );
  }
  return children;
};

const mark = (d: string): SVGElement => svg('path', { class: 'badge__mark', d });
const line = (d: string): SVGElement => svg('path', { class: 'badge__line', d });
const text = (value: string): SVGElement => svg('text', { class: 'badge__text', y: 5 }, [value]);

/** A plain mark, centered on zero. The inspector uses it for a tag with no trait row. */
export const createMarkSymbol = mark;

/** The star of energy as the mark of a badge. */
export const createEnergySymbol = (): SVGElement => mark(energyStarPath(ENERGY_MARK_RADIUS));

/** What the badge of a trait wears. The inspector puts the same mark on its tag. */
export const createTraitSymbol = (piece: Piece, trait: Trait): SVGElement => {
  const { symbol } = trait;
  return symbol.style === 'mark'
    ? mark(symbol.d)
    : symbol.style === 'line'
      ? line(symbol.d)
      : symbol.style === 'text'
        ? text(symbol.value)
        : text(String(traitCount(piece, trait)));
};

/** What the badge of a learned ability wears: the piece that moves that way, or a mark. */
export const createAbilitySymbol = (ability: Ability, owner: Owner): SVGElement => {
  const glyphKind = ABILITY_GLYPH[ability];
  if (!glyphKind) return ability === 'hop' ? line(SPRING) : mark(BALL);
  const mini = createMiniGlyph(glyphKind, owner);
  mini.setAttribute(
    'transform',
    `translate(${-GLYPH_BOX * MINI_SCALE * 0.5} ${-GLYPH_BOX * MINI_SCALE * 0.5}) scale(${MINI_SCALE})`,
  );
  return mini;
};

const traitBadge = (piece: Piece, trait: Trait): BadgeSpec => {
  const count = traitCount(piece, trait);
  const spec = {
    key: `trait-${trait.key}`,
    content: `${trait.tone}:${count}`,
    tone: trait.tone,
    symbol: createTraitSymbol(piece, trait),
  };
  const urgent = trait.blinkAtCount !== undefined && count === trait.blinkAtCount;
  const shaped = { ...spec, fuse: trait.fuse === true, urgent };
  return trait.showCount ? { ...shaped, count } : shaped;
};

/** The tone of the badge of a learned ability. A move with no piece of its own has a mark, not a glyph. */
export const abilityTone = (ability: Ability, temporary = false): string =>
  !ABILITY_GLYPH[ability] ? 'learned' : temporary ? 'temporary' : 'ability';

const abilityBadge = (ability: Ability, piece: Piece, temporary: boolean): BadgeSpec => {
  const tone = abilityTone(ability, temporary);
  return {
    key: `ability-${ability}${temporary ? '-temporary' : ''}`,
    content: `${tone}:${piece.owner}`,
    tone,
    symbol: createAbilitySymbol(ability, piece.owner),
  };
};

const row = (specs: BadgeSpec[], startX: number, y: number, direction: 1 | -1): PlacedBadge[] =>
  specs.map((spec, index) => ({
    ...spec,
    x: startX + direction * index * BADGE_STEP,
    y,
    scale: 1,
    others: 0,
  }));

/** The first badge of a corner, large, for a small board. */
const cornerBadge = (specs: BadgeSpec[], x: number, y: number): PlacedBadge[] => {
  const [first] = specs;
  if (!first) return [];
  const others = specs.length - 1;
  return [{ ...first, content: `${first.content}+${others}`, x, y, scale: DENSE_SCALE, others }];
};

/** What the badge of a pitch wears: its icon. */
export const createPitchSymbol = (pitch: Pitch): SVGElement => mark(PITCH_ICON_PATHS[pitch]);

/**
 * The badges of the roles of a piece in Godsmode: the caster it carries and
 * the pitch it gives. They show on the board what the panel beside it lists.
 * The energy a piece holds has no badge. Its slots are on its shape, see
 * `PieceGlyph.ts`. It was a number on a disc until 2026-10-13. A pawn
 * wears the sign of its caster on its shape, so it has no caster badge.
 */
const roleBadges = (piece: Piece): BadgeSpec[] => {
  const badges: BadgeSpec[] = [];
  if (piece.casterId !== null && !wearsCaster(piece.kind)) {
    badges.push({
      key: 'caster',
      content: `caster:${piece.casterId}`,
      tone: 'caster',
      symbol: svg('g'),
      face: createCasterBadge(piece.casterId),
    });
  }
  if (piece.pitch !== null) {
    badges.push({
      key: 'pitch',
      content: `pitch:${piece.pitch}`,
      tone: `pitch-${piece.pitch}`,
      symbol: createPitchSymbol(piece.pitch),
    });
  }
  return badges;
};

/** Every badge of a piece, with its place in the square. `dense` is for a small board. */
const placeBadges = (piece: Piece, dense: boolean): PlacedBadge[] => {
  const far = GLYPH_BOX - EDGE;

  // Top left: lives, then the traits that fire on capture.
  const topLeft: BadgeSpec[] = [];
  for (let life = 1; life < piece.lives; life += 1) {
    topLeft.push({ key: `life-${life}`, content: 'life', tone: 'life', symbol: mark(HEART_PATH) });
  }
  for (const trait of traitsInCorner(piece, 'capture')) topLeft.push(traitBadge(piece, trait));

  // Top right: learned abilities, and the temporary jump.
  const learned: BadgeSpec[] = piece.abilities
    .filter((ability) => !BASE_ABILITIES[piece.kind].includes(ability))
    .map((ability) => abilityBadge(ability, piece, false));
  if (piece.knightTurns > 0 && !piece.abilities.includes('knight')) {
    const temporary = abilityBadge('knight', piece, true);
    learned.push({
      ...temporary,
      content: `${temporary.content}:${piece.knightTurns}`,
      count: piece.knightTurns,
    });
  }

  // Bottom left: conditions that change how it moves.
  const conditions = traitsInCorner(piece, 'condition').map((trait) => traitBadge(piece, trait));

  // Bottom right: the roles of the piece, then royalty, then the timers that end the piece.
  const bottomRight: BadgeSpec[] = [...roleBadges(piece)];
  if (piece.royal && piece.kind !== 'king') {
    bottomRight.push({ key: 'royal', content: 'royal', tone: 'royal', symbol: mark(CROWN_PATH) });
  }
  for (const trait of traitsInCorner(piece, 'royalty')) bottomRight.push(traitBadge(piece, trait));

  if (dense) {
    const denseFar = GLYPH_BOX - DENSE_EDGE;
    return [
      ...cornerBadge(topLeft, DENSE_EDGE, DENSE_EDGE),
      ...cornerBadge(learned, denseFar, DENSE_EDGE),
      ...cornerBadge(conditions, DENSE_EDGE, denseFar),
      ...cornerBadge(bottomRight, denseFar, denseFar),
    ];
  }
  return [
    ...row(topLeft, EDGE, EDGE, 1),
    ...row(learned, far, EDGE, -1),
    ...row(conditions, EDGE, far, 1),
    ...row(bottomRight, far, far, -1),
  ];
};

/** One badge on the board. The slot holds the place. The face pops in and holds the picture. */
type Slot = { slot: SVGGElement; face: SVGGElement; content: string };

const place = (badge: PlacedBadge): string =>
  `translate(${badge.x}px, ${badge.y}px) scale(${badge.scale})`;

/** Whether a draw put a badge on the piece, and whether it took one off. */
export type BadgeChange = { gained: boolean; ended: boolean };

export type BadgeSet = {
  group: SVGGElement;
  /** `dense` draws one large badge for each corner, for a small board. Says what the draw changed. */
  draw: (piece: Piece, dense: boolean) => BadgeChange;
};

/**
 * The badges of one piece. A draw keeps each badge that is still there, so
 * only a new badge pops, and a badge whose neighbor went slides to its new
 * place. The place is a style on the slot and the pop is an animation on the
 * face. One element with both would lose its place while the pop plays.
 */
export const createBadgeSet = (): BadgeSet => {
  const group = svg('g', { class: 'badges' });
  const slots = new Map<string, Slot>();

  const draw = (piece: Piece, dense: boolean): BadgeChange => {
    const placed = placeBadges(piece, dense);
    const keys = new Set(placed.map((badge) => badge.key));
    const change = { gained: false, ended: false };

    for (const [key, entry] of slots) {
      if (keys.has(key)) continue;
      slots.delete(key);
      change.ended = true;
      entry.slot.classList.add('badge-slot--gone');
      setTimeout(() => entry.slot.remove(), PIECE_LEAVE_MS);
    }

    for (const badge of placed) {
      let entry = slots.get(badge.key);
      if (!entry) {
        const face = svg('g', { class: 'badge' });
        const slot = svg('g', { class: 'badge-slot' }, [face]);
        entry = { slot, face, content: '' };
        slots.set(badge.key, entry);
        change.gained = true;
        // The place is set before the slot joins the page, so a new badge does not slide in.
        slot.style.transform = place(badge);
        group.appendChild(slot);
      }
      entry.slot.style.transform = place(badge);
      if (entry.content !== badge.content) {
        entry.content = badge.content;
        entry.face.setAttribute('class', badgeClass(badge.tone, badge.urgent));
        entry.face.replaceChildren(...badgeParts(badge));
      }
    }
    return change;
  };

  return { group, draw };
};
